import { useEffect, useRef, useState } from 'react';
import { isEmpty, isEqual, merge, values } from 'lodash';

import { useDataSource } from './ApiDataService';
import { useFilterState } from '../stores/FilterStateStore';
import { useIsLoading } from '../components/page-framework/panel/Panel';

/**
 * @typedef DataSourceParams
 * @property {string | string[]} entityFields
 * @property {string | string[]} metrics
 * @property {string} [type]
 * @property {string | string[]} [trends]
 * @property {string} [renderAsEntity=true]
 */

/**
 * @typedef DataSource
 * @property {string} url              The Query Engine URL
 * @property {DataSourceParams} params The parameters to pass in. This is required
 */

function mergeParams(filterState, params) {
    // TODO: Handle duplicate parameters
    return merge({}, params || {}, ...values(filterState?.parameters));
}

/**
 * @template T
 *
 * @param {DataSource} dataSource
 * @param {{[index: string]: string | number | null}} [params]
 * @returns {T?}
 */
export default function usePanelDataService(dataSource, params) {
    const [filterState] = useFilterState();
    const [, /* Not used */ setIsLoading] = useIsLoading();
    const [dataSourceParams, setDataSourceParams] = useState(undefined);
    const previousParams = useRef();
    const [data, isLoading] = useDataSource(dataSource, dataSourceParams);

    // Merge the panel's filter state params with the optional params to provide a single set of
    // parameters for the data service. These parameters will be combined with the static data
    // source parameters when making the query.
    useEffect(() => {
        if (!filterState) return;

        const newParams = mergeParams(filterState, params);

        // Make sure we have params and that they have actually changed.  This avoids unnecessary
        // queries.
        if (isEmpty(newParams) || isEqual(previousParams.current, newParams)) return;

        previousParams.current = newParams;

        setDataSourceParams(newParams);
    }, [filterState, params]);

    // Show the panel's loading indicator when the data is being loaded.
    useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading, setIsLoading]);

    return data;
}
